import Constants from 'expo-constants';
import {
  isDirectPhotoUrl,
  isPlacesApiPhotoUrl,
  parsePlacePhotoRef,
} from '../lib/place-photo-url';
import type { Place } from '@my-fullstack-app/itinerary-types';
import { quantizePhotoWidth } from '@my-fullstack-app/trpc-types';

/**
 * Returns a working photo URL for a place.
 *
 * Routes everything through `/api/places/photo?placeId=...&photoRef=...` —
 * a server-side proxy that resolves Google's signed photoUri once,
 * 302-redirects to it, and emits `Cache-Control: public, max-age=3600`. The
 * proxy URL itself is STABLE for a given (placeId, photoRef), so:
 *   - Browser HTTP cache hits the same URL across mounts AND sessions.
 *   - On native, expo-image's disk cache keys on the proxy URL.
 *   - When the browser cache eventually expires, the next hit lands on our
 *     in-memory server cache (50 min TTL) — only a true cold start reaches
 *     Google.
 *
 * No tRPC call is needed; the URL is built synchronously. Falls back to
 * the legacy persisted `photoUrl` only when no `photoRef` is available.
 *
 * Accepts `null` so callers that only sometimes have a place (e.g.
 * polymorphic entity previews) can call the hook unconditionally without
 * violating the rules of hooks.
 */

function getApiBaseUrl(): string {
  const fromConfig = Constants.expoConfig?.extra?.apiUrl;
  if (typeof fromConfig === 'string' && fromConfig.length > 0) return fromConfig;
  if (typeof process !== 'undefined' && process.env.EXPO_PUBLIC_API_URL) {
    return process.env.EXPO_PUBLIC_API_URL;
  }
  // Same-origin on web, default to localhost on native dev.
  if (typeof window !== 'undefined') return '';
  return 'http://localhost:3000';
}

interface ResolveOptions {
  skip?: boolean | undefined;
  /** Desired source width in px. Higher = sharper on big screens; lower
   *  = less bandwidth on small thumbnails. Server clamps to [200, 1600].
   *  Defaults to 1200 (retina-quality for most rows; explicit 1600 for
   *  full-bleed heroes on desktop). */
  width?: number | undefined;
}

/**
 * Pure variant of `usePlacePhotoUrl` for callers that aren't React
 * components / hooks (adapters, factory functions). Identical logic;
 * the hook delegates here.
 *
 * Backwards-compatible: the second arg can still be a boolean `skip`,
 * matching the previous signature so existing callers keep working.
 */
export function resolvePlacePhotoUrl(
  place: Place | null,
  optsOrSkip?: boolean | ResolveOptions | undefined,
): string | undefined {
  const opts: ResolveOptions = typeof optsOrSkip === 'boolean' ? { skip: optsOrSkip } : (optsOrSkip ?? {});
  if (!place || opts.skip) return undefined;

  // ADR-0044: prefer the neutral photoToken when present. Built by the
  // store/sync backfill from legacy placeId+photoRef, or set directly
  // by new writes from the catalog adapter.
  if (place.photoToken) {
    return resolveCatalogPhotoUrl(place.photoToken, opts.width !== undefined ? { width: opts.width } : {});
  }

  // `parsePlacePhotoRef` can recover placeId from the URL itself for
  // v1-shaped URLs (`/places/X/photos/Y/media`), so a Place persisted
  // without an explicit `placeId` (catalog-adapter sourced) still
  // gets routed through the proxy instead of leaking a direct v1
  // Google fetch — which would bill the v1 Place Details Photos SKU.
  const parsed = parsePlacePhotoRef(place.photoUrl, place.placeId);
  // Runtime fallback even though Place.placeId is required — legacy
  // persisted records (and tests) hit this path without a placeId set;
  // parsing the v1 photo URL recovers it.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const placeId = place.placeId ?? parsed?.placeId;
  const photoRef = place.photoRef ?? parsed?.photoRef;
  if (placeId && photoRef) {
    const base = getApiBaseUrl();
    const params = new URLSearchParams({ placeId, photoRef });
    // Snap to the shared width ladder so callers passing a live viewport
    // width (`Math.ceil(width)`, `viewportWidth * 2`, …) collapse onto a
    // handful of stable proxy URLs instead of one per pixel — keeps the
    // browser / CDN cache shared across users and the server cache key +
    // Google Place Photos billing bounded. See `quantizePhotoWidth`.
    if (opts.width != null) params.set('w', String(quantizePhotoWidth(opts.width)));
    return `${base}/api/places/photo?${params.toString()}`;
  }
  if (place.photoUrl && isDirectPhotoUrl(place.photoUrl)) return place.photoUrl;
  // Last line of defence: never hand the client a raw Google API URL.
  // Hitting `places.googleapis.com/v1/.../media` directly bills the v1
  // photos SKU at $7/1k; the legacy `maps.googleapis.com` endpoint
  // bills the legacy Photo SKU at $7/1k. If we couldn't build a proxy
  // URL above, return undefined so the caller falls back to a
  // gradient placeholder instead.
  if (place.photoUrl && isPlacesApiPhotoUrl(place.photoUrl)) return undefined;
  return place.photoUrl;
}

export function usePlacePhotoUrl(
  place: Place | null,
  optsOrSkip?: boolean | ResolveOptions | undefined,
): string | undefined {
  return resolvePlacePhotoUrl(place, optsOrSkip);
}

/**
 * ADR-0044 token-based variant. Given a vendor-neutral `photoToken`
 * from `PlaceCatalogEntry`, build the stable proxy URL that resolves
 * to a signed source URL via `/api/places/photo?token=...`. Same
 * cache-key shape as the legacy URL — same browser HTTP cache, same
 * server in-memory cache.
 */
export function resolveCatalogPhotoUrl(
  photoToken: string | null | undefined,
  opts?: { width?: number } | undefined,
): string | undefined {
  if (!photoToken) return undefined;
  const base = getApiBaseUrl();
  const params = new URLSearchParams({ token: photoToken });
  // Same width-ladder snap as the legacy placeId/photoRef path — keeps the
  // proxy URL stable across users at similar sizes. See `quantizePhotoWidth`.
  if (opts?.width != null) params.set('w', String(quantizePhotoWidth(opts.width)));
  return `${base}/api/places/photo?${params.toString()}`;
}
